import { ExternalLink, ShoppingBag, Disc3, QrCode } from "lucide-react";
import { useEffect, useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import ExclusiveDownloads from "@/components/ExclusiveDownloads";
import NewsletterSignup from "@/components/NewsletterSignup";
import {
  OVA40_VINYL_PHYSICAL as OVA40_PHYSICAL,
  OVA40_VINYL_QR as OVA40_QR,
  OVA40_COVER as VINYL,
} from "@/lib/assets";

const BIZ =
  "https://images.unsplash.com/photo-1770062421988-7929b4748e29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtdXNsaW0lMjBidXNpbmVzc3dvbWFuJTIwZGFyayUyMGx1eHVyeXxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";
const VIBE =
  "https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBwdXJwbGUlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";

// Pre-order opens May 23 2026 · Album ships May 30 2026
const PREORDER_OPENS = new Date("2026-05-23T00:00:00-04:00");
const RELEASE_DATE = new Date("2026-05-30T00:00:00-04:00");
const ELASTICSTAGE_URL = "https://elasticstage.com/ninacapone/releases/ova-40-album";

const SHOP_ITEMS = [
  {
    id: "ova40-vinyl",
    name: "OVA 40 — Vinyl + CD",
    price: "Pre-Order · May 23",
    type: "Limited Physical Release",
    image: OVA40_PHYSICAL,
    description:
      "The 17-track debut, pressed on classic black vinyl + collectible CD. Limited-run physical edition with the official OVA 40 artwork. Pre-orders open May 23 · ships May 30.",
    href: ELASTICSTAGE_URL,
    cta: "Pre-Order Hard Copies",
    highlight: true,
  },
  {
    id: "ova40-stream",
    name: "OVA 40 — Stream & Save",
    price: "Out Now",
    type: "Digital · Streaming",
    image: VINYL,
    description:
      "Stream the 17-track debut on Spotify, Apple Music, and every major DSP via the Hyperfollow smart link.",
    href: "https://distrokid.com/hyperfollow/ninacapone/ova-40",
    cta: "Stream & Save",
  },
  {
    id: "ceo-hijab",
    name: "In Da Streets Radio Merch",
    price: "From $24",
    type: "Branded Apparel",
    image: BIZ,
    description:
      "Official In Da Streets Radio merchandise — tees, hoodies, and station drops shipped via Square.",
    href: "https://idsrswagshop.square.site/",
    cta: "Shop The Drop",
  },
  {
    id: "merch",
    name: "Official Nina Capone™ Merchandise",
    price: "From $30",
    type: "Official Artist Merch",
    image: VIBE,
    description:
      "Official Nina Capone™ merch and limited-run drops. Branded streetwear shipped worldwide.",
    href: "https://idsrswagshop.square.site/",
    cta: "Shop Merch",
  },
];

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  return { diff, days, hours };
}

export default function Shop() {
  const preOpens = useCountdown(PREORDER_OPENS);
  const release = useCountdown(RELEASE_DATE);
  const preorderLive = preOpens.diff === 0;

  return (
    <div data-testid="shop-page" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-6">
            <SectionLabel number="04" label="Shop" />
            <h1 className="font-serif font-light tracking-tighter text-[2.8rem] sm:text-6xl lg:text-8xl leading-[0.95]">
              The
              <br />
              <em className="italic" style={{ color: "var(--gold)" }}>
                Catalog.
              </em>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-white/60 leading-relaxed">
              Music, books, and merch — every order ships from indastreetsradio.com.
              A portion of every sale funds Inspire Da Streets programs.
            </p>
          </div>
        </div>

        {/* OVA 40 PHYSICAL PRE-ORDER — HERO STRIP */}
        <section
          className="relative surface overflow-hidden mb-12 lg:mb-16"
          data-testid="ova40-physical-hero"
        >
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url(${OVA40_PHYSICAL})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(50px) saturate(1.2)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,3,8,0.78) 0%, rgba(10,5,15,0.92) 100%)",
            }}
          />
          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-14 p-8 lg:p-14 items-center">
            <div className="lg:col-span-5">
              <div className="relative aspect-[3/4] max-w-[420px] mx-auto lg:mx-0 surface overflow-hidden">
                <img
                  src={OVA40_PHYSICAL}
                  alt="OVA 40 vinyl + CD physical release — Nina Capone"
                  className="w-full h-full object-cover"
                  data-testid="ova40-physical-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] border"
                  style={{
                    color: "var(--gold)",
                    borderColor: "rgba(232,180,184,0.5)",
                  }}
                  data-testid="ova40-physical-badge"
                >
                  <Disc3 size={11} />
                  Limited Physical Release
                </span>
                {preorderLive ? (
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] border live-dot"
                    style={{
                      color: "var(--gold)",
                      borderColor: "rgba(232,180,184,0.8)",
                      background: "rgba(232,180,184,0.08)",
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full live-dot"
                      style={{ background: "var(--gold)" }}
                    />
                    Pre-Order Live
                  </span>
                ) : (
                  <span className="eyebrow">Pre-Orders Open May 23 · Ships May 30</span>
                )}
              </div>

              <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
                OVA 40 on{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Vinyl + CD.
                </em>
              </h2>
              <p className="font-serif italic text-lg lg:text-xl text-white/75 leading-relaxed max-w-2xl">
                "Some records belong on a shelf. This is one of them."
              </p>
              <p className="text-white/65 text-base leading-relaxed max-w-2xl">
                The seventeen-track debut pressed on classic black vinyl + collectible CD —
                printed with the official OVA 40 artwork. Limited run, manufactured on-demand by
                Elastic Stage. Pre-orders open <strong style={{ color: "var(--gold)" }}>May 23</strong>,
                ships <strong style={{ color: "var(--gold)" }}>May 30</strong>.
              </p>

              {/* Countdown */}
              <div
                className="grid grid-cols-3 gap-3 sm:gap-4 max-w-md"
                data-testid="ova40-physical-countdown"
              >
                <CountdownCell
                  k={preorderLive ? release.days : preOpens.days}
                  label="Days"
                />
                <CountdownCell
                  k={preorderLive ? release.hours : preOpens.hours}
                  label="Hours"
                />
                <CountdownCell
                  k={preorderLive ? "→ May 30" : "→ May 23"}
                  label={preorderLive ? "Ships" : "Pre-Order Opens"}
                  small
                />
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 pt-2">
                <a
                  href={ELASTICSTAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-3 text-xs"
                  data-testid="ova40-physical-preorder"
                >
                  {preorderLive ? "Pre-Order Now" : "Set My Pre-Order Reminder"}
                  <ExternalLink size={14} />
                </a>
                <a
                  href={ELASTICSTAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center justify-center gap-2 px-7 py-3 text-xs"
                  data-testid="ova40-physical-elasticstage"
                >
                  View On Elastic Stage
                </a>
              </div>

              {/* QR for in-person sharing */}
              <div className="flex items-center gap-4 pt-5 mt-3 border-t border-white/10">
                <a
                  href={ELASTICSTAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 w-20 h-20 rounded-md bg-white p-1.5 hover:scale-[1.04] transition-transform"
                  aria-label="Scan to pre-order OVA 40 vinyl + CD"
                  data-testid="ova40-physical-qr"
                >
                  <img
                    src={OVA40_QR}
                    alt="QR — Pre-order OVA 40 vinyl + CD"
                    className="w-full h-full object-contain"
                  />
                </a>
                <div className="text-xs text-white/60 leading-relaxed">
                  <span
                    className="inline-flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-[0.25em]"
                    style={{ color: "var(--gold)" }}
                  >
                    <QrCode size={11} /> Scan To Pre-Order
                  </span>
                  <p className="text-white/55">
                    Snap this from a flyer, sticker, or in-store display. Goes
                    straight to the Elastic Stage pre-order page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ExclusiveDownloads variant="full" limit={9} />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
          {SHOP_ITEMS.map((item) => (
            <a
              key={item.id}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`surface group block ${item.highlight ? "ring-1 ring-[color:var(--gold)]/40" : ""}`}
              data-testid={`shop-item-${item.id}`}
            >
              <div
                className="aspect-square bg-cover bg-center"
                style={{ backgroundImage: `url(${item.image})` }}
              />
              <div className="p-7 lg:p-8 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="eyebrow">{item.type}</p>
                  {item.highlight ? (
                    <Disc3 size={16} style={{ color: "var(--gold)" }} />
                  ) : (
                    <ShoppingBag size={16} style={{ color: "var(--gold)" }} />
                  )}
                </div>
                <h3 className="font-serif text-3xl tracking-tighter font-light leading-tight">
                  {item.name}
                </h3>
                <p className="text-sm text-white/55 leading-relaxed">
                  {item.description}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span style={{ color: "var(--gold)" }} className="text-sm font-medium">
                    {item.price}
                  </span>
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 group-hover:text-[color:var(--gold)] transition-colors">
                    {item.cta} <ExternalLink size={12} />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-20 surface p-10 lg:p-14 text-center">
          <p className="eyebrow mb-4">Wholesale & Bulk</p>
          <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light mb-4">
            Stocking the catalog at your shop?
          </h3>
          <p className="text-sm text-white/55 max-w-xl mx-auto mb-8">
            Indie record stores, mosques, community spaces, and bookshops —
            the full Swag Shop ships worldwide via Square.
          </p>
          <a
            href="https://idsrswagshop.square.site/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
            data-testid="shop-wholesale-link"
          >
            Visit The Full Swag Shop
          </a>
        </div>
      </div>

      <NewsletterSignup source="shop-page" testId="shop-newsletter-signup" />
    </div>
  );
}

function CountdownCell({ k, label, small }) {
  return (
    <div
      className="surface px-3 py-3 text-center"
      style={{ background: "rgba(232,180,184,0.06)" }}
    >
      <div
        className={`font-serif tracking-tighter ${small ? "text-base sm:text-lg" : "text-3xl sm:text-4xl"} leading-none`}
        style={{ color: "var(--gold)" }}
      >
        {k}
      </div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-white/55 mt-2">
        {label}
      </div>
    </div>
  );
}
